// Static data for the onboarding funnel's speaking-topic taste: a short
// exchange from the "Cooking with Mum" topic, with per-word gloss,
// transliteration, and hand-written §9 morphology so the step needs no
// backend at all (anonymous visitors, zero quota).
//
// Audio streams from GET /api/onboarding/audio/<key>/ — a public endpoint
// serving only the fixed clips whitelisted in wasl/core/onboarding_audio.py
// (keys below must match it). The component enables play buttons only for
// keys the manifest endpoint reports as servable.

export const TOPIC_DEMO = {
  topicTitle: "Cooking with Mum",
  intro: "Every lesson works like the verse you just tapped. Here's a moment from one of the 40+ speaking topics:",
  lines: [
    {
      key: "l1",
      speaker: "مَرْيَم",
      speakerEn: "Maryam",
      translation: "I'll help you!",
      words: [
        {
          key: "l1w0",
          text: "أَنَا",
          translit: "anā",
          gloss: "I",
          morphology: {
            label: "أَنَا — I",
            segments: [
              { type: "root", text: "أَنَا", labelAr: "ضمير منفصل", labelEn: "detached pronoun", side: "bottom" },
            ],
            irab: {
              statusAr: "ضمير مبني في محل رفع مبتدأ",
              statusEn: "Pronoun — the subject of the sentence (mubtadaʼ).",
            },
          },
        },
        {
          key: "l1w1",
          text: "سَأُسَاعِدُكِ!",
          translit: "sa-usāʿiduki",
          gloss: "I will help you",
          morphology: {
            label: "سَأُسَاعِدُكِ — I will help you",
            segments: [
              { type: "prefix", text: "سَـ", labelAr: "حرف استقبال", labelEn: "future marker", side: "top" },
              { type: "root", text: "أُسَاعِدُ", labelAr: "فعل مضارع — الجذر: س-ع-د", labelEn: "present-tense verb · root s-ʿ-d", side: "bottom", hasCase: true },
              { type: "pronoun", text: "كِ", labelAr: "ضمير متصل", labelEn: "attached pronoun — “you”", side: "top" },
            ],
            irab: {
              statusAr: "فعل مضارع مرفوع وعلامة رفعه الضمة، والفاعل مستتر تقديره أنا، والكاف ضمير متصل في محل نصب مفعول به",
              statusEn: "Present verb, nominative (ḍammah); hidden subject “I”; the attached كِ is the object — “you”.",
            },
          },
        },
      ],
    },
    {
      key: "l2",
      speaker: "عُمَر",
      speakerEn: "Omar",
      translation: "Me too! What do we do first?",
      words: [
        {
          key: "l2w0",
          text: "وَأَنَا",
          translit: "wa-anā",
          gloss: "and I (me too)",
          morphology: {
            label: "وَأَنَا — and I",
            segments: [
              { type: "prefix", text: "وَ", labelAr: "حرف عطف", labelEn: "conjunction — “and”", side: "top" },
              { type: "root", text: "أَنَا", labelAr: "ضمير منفصل", labelEn: "detached pronoun", side: "bottom" },
            ],
            irab: {
              statusAr: "الواو عاطفة، والضمير مبني في محل رفع مبتدأ",
              statusEn: "The و joins the sentences; the pronoun is the subject.",
            },
          },
        },
        {
          key: "l2w1",
          text: "أَيْضًا!",
          translit: "ayḍan",
          gloss: "also / too",
          morphology: {
            label: "أَيْضًا — also",
            segments: [
              { type: "root", text: "أَيْضًا", labelAr: "اسم — مفعول مطلق", labelEn: "noun — “also”", side: "bottom", hasCase: true },
            ],
            irab: {
              statusAr: "مفعول مطلق منصوب وعلامة نصبه الفتحة",
              statusEn: "“Also” — accusative (fatḥah), used adverbially.",
            },
          },
        },
        {
          key: "l2w2",
          text: "مَاذَا",
          translit: "mādhā",
          gloss: "what?",
          morphology: {
            label: "مَاذَا — what?",
            segments: [
              { type: "root", text: "مَاذَا", labelAr: "اسم استفهام", labelEn: "interrogative — “what”", side: "bottom" },
            ],
            irab: {
              statusAr: "اسم استفهام مبني في محل نصب مفعول به مقدَّم",
              statusEn: "Question word “what” — the fronted object of the verb.",
            },
          },
        },
        {
          key: "l2w3",
          text: "نَفْعَلُ",
          translit: "nafʿalu",
          gloss: "we do",
          morphology: {
            label: "نَفْعَلُ — we do",
            segments: [
              { type: "root", text: "نَفْعَلُ", labelAr: "فعل مضارع — الجذر: ف-ع-ل", labelEn: "present-tense verb · root f-ʿ-l", side: "bottom", hasCase: true },
            ],
            irab: {
              statusAr: "فعل مضارع مرفوع وعلامة رفعه الضمة، والفاعل ضمير مستتر تقديره نحن",
              statusEn: "Present verb, nominative (ḍammah); hidden subject “we”.",
            },
          },
        },
        {
          key: "l2w4",
          text: "أَوَّلًا؟",
          translit: "awwalan",
          gloss: "first",
          morphology: {
            label: "أَوَّلًا — first",
            segments: [
              { type: "root", text: "أَوَّلًا", labelAr: "ظرف زمان", labelEn: "adverb of time — “first”", side: "bottom", hasCase: true },
            ],
            irab: {
              statusAr: "ظرف زمان منصوب وعلامة نصبه الفتحة",
              statusEn: "“First” — accusative adverb of time (fatḥah).",
            },
          },
        },
      ],
    },
  ],
};
